import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Form } from 'react-bootstrap';
import ApprovalsPanel, { APPROVALS_CHANGED, APPROVAL_APPLIED, approvalsBadge } from './ApprovalsPanel';
import './Header.css'; // Assuming styles are moved to a CSS file

const Header = ({ isLoggedIn, onLogOut, userName, userType, addLogEntry, logoPath = '/CGCLogo.png' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
    const [savingEmailSetting, setSavingEmailSetting] = useState(false);
    const dropdownRef = useRef(null);
    // Autorizaciones (admins decide, sales follow their own requests): the count shows on the menu button and its entry.
    const [showApprovalsModal, setShowApprovalsModal] = useState(false);
    const [approvalCount, setApprovalCount] = useState(0);
    const usesApprovals = isLoggedIn && (userType === 'admin' || userType === 'sales');

    useEffect(() => {
        if (!usesApprovals) { setApprovalCount(0); return undefined; }
        const load = async () => {
            try {
                const res = await fetch('/api/approvals');
                if (res.ok) setApprovalCount(approvalsBadge(await res.json()));
            } catch (err) {
                console.error('❌ Error fetching approvals:', err);
            }
        };
        load();
        const timer = setInterval(load, 60000);
        window.addEventListener(APPROVALS_CHANGED, load);
        return () => { clearInterval(timer); window.removeEventListener(APPROVALS_CHANGED, load); };
    }, [usesApprovals, location.pathname]);

    const handleApprovalDecided = (applied) => {
        window.dispatchEvent(new Event(APPROVALS_CHANGED));
        if (applied) window.dispatchEvent(new Event(APPROVAL_APPLIED));
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogOutClick = () => {
        onLogOut();
    };

    const openNotificationsModal = async () => {
        setDropdownOpen(false);
        setShowNotificationsModal(true);
        try {
            const res = await fetch('/api/settings');
            if (!res.ok) return;
            const data = await res.json();
            setEmailNotificationsEnabled(!!data.emailNotificationsEnabled);
        } catch (err) {
            console.error('❌ Error fetching settings:', err);
        }
    };

    const handleToggleEmailNotifications = async () => {
        const next = !emailNotificationsEnabled;
        setEmailNotificationsEnabled(next);
        setSavingEmailSetting(true);
        try {
            const res = await fetch('/api/settings', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emailNotificationsEnabled: next }),
            });
            if (!res.ok) throw new Error('Failed to update settings');
        } catch (err) {
            console.error('❌ Error updating settings:', err);
            setEmailNotificationsEnabled(!next);
        } finally {
            setSavingEmailSetting(false);
        }
    };

    return (
        <header className="header">
            <div className="logo-container">
                <img src={logoPath} alt="Caribbean Gas Company Logo" className="logo" />
            </div>

            {isLoggedIn && (
                <div className="user-controls" ref={dropdownRef}>
                    <button
                        type="button"
                        className="user-greeting-btn"
                        onClick={() => setDropdownOpen((open) => !open)}
                    >
                        Hola, {userName}
                        {approvalCount > 0 && <span className="user-menu-badge">{approvalCount}</span>}
                        {' '}<span className="user-dropdown-arrow">▾</span>
                    </button>

                    {dropdownOpen && (
                        <div className="user-dropdown-menu">
                            {userType === 'admin' && (
                                <button type="button" className="user-dropdown-item" onClick={openNotificationsModal}>
                                    Notificaciones
                                </button>
                            )}
                            {usesApprovals && (
                                <button
                                    type="button"
                                    className="user-dropdown-item"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        setShowApprovalsModal(true);
                                    }}
                                >
                                    Autorizaciones
                                    {approvalCount > 0 && <span className="user-menu-badge">{approvalCount}</span>}
                                </button>
                            )}
                            {/* Admins can open the sales screen too (and come back). */}
                            {userType === 'admin' && location.pathname !== '/admin' && (
                                <button
                                    type="button"
                                    className="user-dropdown-item"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate('/admin');
                                    }}
                                >
                                    Panel de Administración
                                </button>
                            )}
                            {userType === 'admin' && location.pathname !== '/sales' && (
                                <button
                                    type="button"
                                    className="user-dropdown-item"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate('/sales');
                                    }}
                                >
                                    Panel de Ventas
                                </button>
                            )}
                            {userType === 'admin' && (
                                <button
                                    type="button"
                                    className="user-dropdown-item"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate('/admin/reportes');
                                    }}
                                >
                                    Reportes
                                </button>
                            )}
                        </div>
                    )}

                    <button className="btn btn-danger" onClick={handleLogOutClick}>
                        Cerrar sesión
                    </button>
                </div>
            )}

            <Modal show={showApprovalsModal} onHide={() => setShowApprovalsModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{userType === 'admin' ? 'Autorizaciones' : 'Mis Solicitudes de Autorización'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ApprovalsPanel onDecided={handleApprovalDecided} />
                </Modal.Body>
            </Modal>

            <Modal show={showNotificationsModal} onHide={() => setShowNotificationsModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Notificaciones</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Check
                        type="switch"
                        id="email-notifications-switch"
                        label="Notificaciones por correo"
                        checked={emailNotificationsEnabled}
                        disabled={savingEmailSetting}
                        onChange={handleToggleEmailNotifications}
                    />
                    <small className="text-muted d-block mt-2">
                        Cuando está desactivado, los eventos (ajustes de inventario, contribuciones y despachos) solo se notifican por push, no por correo.
                    </small>
                </Modal.Body>
            </Modal>
        </header>
    );
};

export default Header;
